import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, ShieldCheck, CheckCircle, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../supabaseClient';
import './AboutPage.css';

// About page — honesty rebuild (revised).
// Five stacked sections:
//   1. Hero               — unchanged
//   2. What Makes Us Different — 4 cream cards with gold border + gold icons
//   3. Founder            — placeholder slot, unchanged
//   4. What We Cover      — 3 stat columns (live listing count from Supabase)
//   5. Get Started        — 3 cream CTA cards; agents CTA links to apply form
//
// Sharp corners everywhere (border-radius: 0). Ink + gold + warm cream
// palette. No fabricated claims.

export default function AboutPage() {
  const { t } = useLanguage();

  // Live count of non-archived listings. Starts as null → renders an em-dash
  // while the request is in flight, then the actual number (no "+" suffix).
  const [listingCount, setListingCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { count, error } = await supabase
        .from('listings')
        .select('id', { count: 'exact', head: true })
        .not('status', 'in', '(archived,deleted)');
      if (cancelled) return;
      if (error) return;
      setListingCount(typeof count === 'number' ? count : 0);
    })();
    return () => { cancelled = true; };
  }, []);

  const differentiators = [
    {
      Icon: BarChart3,
      title: t('about.diff1Title'),
      body: t('about.diff1Body'),
    },
    {
      Icon: ShieldCheck,
      title: t('about.diff2Title'),
      body: t('about.diff2Body'),
    },
    {
      Icon: CheckCircle,
      title: t('about.diff3Title'),
      body: t('about.diff3Body'),
    },
    {
      Icon: MapPin,
      title: t('about.diff4Title'),
      body: t('about.diff4Body'),
    },
  ];

  const coverage = [
    { value: '5',                                                label: t('about.coverageDistricts') },
    { value: listingCount === null ? '—' : String(listingCount), label: t('about.coverageListings') },
    { value: '2026',                                             label: t('about.coverageFounded') },
  ];

  const cta = [
    {
      heading: t('about.ctaBuyersHeading'),
      body: t('about.ctaBuyersBody'),
      button: t('about.ctaBuyersBtn'),
      style: 'outline',
      to: '/listings',
    },
    {
      heading: t('about.ctaSellersHeading'),
      body: t('about.ctaSellersBody'),
      button: t('about.ctaSellersBtn'),
      style: 'outline',
      to: '/list-property',
    },
    {
      heading: t('about.ctaAgentsHeading'),
      body: t('about.ctaAgentsBody'),
      button: t('about.ctaAgentsBtn'),
      style: 'outline',
      // Opens the BrokerApplicationModal directly on /find-agent via ?apply=1.
      to: '/find-agent?apply=1',
    },
  ];

  return (
    <div className="ab2-page">
      <div className="ab2-container">

        {/* ────────────────────────────────────────────────────────────
            Section 1 — Hero (unchanged)
            ──────────────────────────────────────────────────────────── */}
        <section className="ab2-hero">
          <span className="ab2-kicker">{t('about.tag')}</span>
          <h1 className="ab2-hero-title">
            {t('about.title')}<br />
            <span>{t('about.titleHighlight')}</span>
          </h1>
          <p className="ab2-hero-sub">{t('about.sub')}</p>
          <div className="ab2-hero-rule" aria-hidden="true" />
        </section>

        {/* ────────────────────────────────────────────────────────────
            Section 2 — What Makes Us Different (4 cream cards, gold border)
            ──────────────────────────────────────────────────────────── */}
        <section className="ab2-section">
          <span className="ab2-kicker">{t('about.diffSectionTitle')}</span>
          <div className="ab2-diff-grid">
            {differentiators.map(({ Icon, title, body }) => (
              <article key={title} className="ab2-diff-card">
                <Icon size={24} strokeWidth={1.5} color="#B8924A" />
                <h3 className="ab2-diff-card__title">{title}</h3>
                <p className="ab2-diff-card__body">{body}</p>
              </article>
            ))}
          </div>
        </section>

        {/*  ────────────────────────────────────────────────────────────
            Section 3 — Founder. Skipped for now per sign-off — copy
            still needs review. Reinstate when the user confirms the
            three paragraphs from the brief (or a softened version).
            ──────────────────────────────────────────────────────────── */}

        {/* ────────────────────────────────────────────────────────────
            Section 4 — What We Cover (3 stat columns, live listing count)
            ──────────────────────────────────────────────────────────── */}
        <section className="ab2-section">
          <span className="ab2-kicker">{t('about.coverageSectionTitle')}</span>
          <div className="ab2-coverage-grid">
            {coverage.map(({ value, label }) => (
              <div key={label} className="ab2-coverage-cell">
                <div className="ab2-coverage-value">{value}</div>
                <div className="ab2-coverage-label">{label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ────────────────────────────────────────────────────────────
            Section 5 — Get Started (3 cream CTA cards)
            ──────────────────────────────────────────────────────────── */}
        <section className="ab2-section">
          <span className="ab2-kicker">{t('about.getStartedTitle')}</span>
          <div className="ab2-cta-grid">
            {cta.map(({ heading, body, button, style, to }) => (
              <article key={heading} className="ab2-cta-card">
                <h3 className="ab2-cta-card__heading">{heading}</h3>
                <p className="ab2-cta-card__body">{body}</p>
                <Link
                  to={to}
                  className={`ab2-cta-card__btn ab2-cta-card__btn--${style}`}
                >
                  {button}
                </Link>
              </article>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
}
